import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWalletKit } from "@mysten/wallet-kit";
import { TransactionBlock } from "@mysten/sui.js/transactions";
import { SuiClient, getFullnodeUrl } from "@mysten/sui.js/client";
import "./SimpleSwapInterface.css";

type SuiNetwork = "testnet" | "mainnet" | "devnet" | "localnet";

const SUPPORTED_NETWORKS: readonly SuiNetwork[] = [
  "testnet",
  "mainnet",
  "devnet",
  "localnet",
];

const parseNetwork = (value?: string | null): SuiNetwork | null => {
  if (!value) return null;
  const lower = value.toLowerCase() as SuiNetwork;
  return SUPPORTED_NETWORKS.includes(lower) ? lower : null;
};

type SwapRuntimeConfig = {
  network?: string | null;
  globalConfig?: string | null;
  clockObject?: string | null;
  platformFeeMist?: string | null;
  gasBudget?: number | null;
  suiCoinType?: string | null;
};

const DEFAULT_GLOBAL_CONFIG = "0x9774e359588ead122af1c7e7f64e14ade261cfeecdb5d0eb4a5b3b4c8ab8bd3e";
const DEFAULT_CLOCK_OBJECT = "0x6";
const SUI_SYMBOL = "SUI";
const SUI_ICON = "💧";

interface MemeTokenConfig {
  id: string;
  name: string;
  symbol: string;
  description: string;
  image?: string;
  contractPackageId: string;
  bondingCurveId: string;
  metadataId?: string | null;
  coinType: string;
  poolCreated?: boolean;
}

interface SimpleSwapInterfaceProps {
  token: MemeTokenConfig & {
    decimals: number;
    poolCreated?: boolean;
    poolId?: string | null;
    swapPlatformFeeObj?: string | null;
  };
  user?: any;
  userSessionToken?: string | null;
}

type TokenState = {
  id: string;
  name: string;
  symbol: string;
  description: string;
  image?: string;
  contractPackageId: string;
  bondingCurveId: string;
  metadataId?: string | null;
  coinType: string;
  poolCreated: boolean;
  poolId: string | null;
  swapPlatformFeeObj: string | null;
  decimals: number;
};

const normalizeToken = (raw: SimpleSwapInterfaceProps["token"]): TokenState => ({
  id: String(raw.id),
  name: raw.name,
  symbol: raw.symbol,
  description: raw.description,
  image: raw.image,
  contractPackageId: raw.contractPackageId,
  bondingCurveId: raw.bondingCurveId,
  metadataId: raw.metadataId ?? null,
  coinType: raw.coinType,
  poolCreated: Boolean(raw.poolCreated),
  poolId: raw.poolId ?? null,
  swapPlatformFeeObj: raw.swapPlatformFeeObj ?? null,
  decimals: Number.isFinite(raw.decimals) ? raw.decimals : 9,
});

const SimpleSwapInterface: React.FC<SimpleSwapInterfaceProps> = ({
  token,
  user,
  userSessionToken,
}) => {
  const [tokenState, setTokenState] = useState<TokenState>(() => normalizeToken(token));

  useEffect(() => {
    setTokenState((prev) => {
      const normalized = normalizeToken(token);
      if (!prev || prev.id !== normalized.id) {
        return normalized;
      }
      return {
        ...prev,
        ...normalized,
        poolId: normalized.poolId ?? prev.poolId,
        swapPlatformFeeObj: normalized.swapPlatformFeeObj ?? prev.swapPlatformFeeObj,
      };
    });
  }, [token]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "/api";

  const refreshPoolMetadata = useCallback(async (): Promise<TokenState | null> => {
    try {
      const response = await fetch(`${BACKEND_URL}/tokens/${tokenState.id}/sync-pool`, { method: "POST" });
      if (!response.ok) {
        throw new Error(`Failed to sync pool metadata (${response.status})`);
      }
      const payload = await response.json();
      if (payload?.success && payload?.token) {
        let nextState: TokenState | null = null;
        setTokenState((prev): TokenState => {
          nextState = {
            ...prev,
            poolId: payload.token.poolId ?? prev.poolId,
            poolCreated:
              typeof payload.token.poolCreated === "boolean"
                ? payload.token.poolCreated
                : prev.poolCreated,
            swapPlatformFeeObj: payload.token.swapPlatformFeeObj ?? prev.swapPlatformFeeObj,
          };
          return nextState;
        });
        return nextState;
      }
      return null;
    } catch (err) {
      console.warn("Unable to refresh pool metadata", err);
      return null;
    }
  }, [BACKEND_URL, tokenState.id]);

  useEffect(() => {
    refreshPoolMetadata();
  }, [refreshPoolMetadata]);
  const { isConnected, currentAccount, signAndExecuteTransactionBlock } = useWalletKit();
  const [activeTab, setActiveTab] = useState<"swap" | "limit">("swap");
  const [slippage, setSlippage] = useState("0.5");
  const [fromAmount, setFromAmount] = useState("");
  const [toAmount, setToAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<
    | { type: 'idle' }
    | { type: 'loading'; message: string }
    | { type: 'success'; message: string; txDigest: string | null }
    | { type: 'error'; message: string }
  >({ type: 'idle' });
  const [runtimeConfig, setRuntimeConfig] = useState<SwapRuntimeConfig | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [swapDirection, setSwapDirection] = useState<"b2a" | "a2b">("b2a");

  const envNetwork = parseNetwork(process.env.REACT_APP_SUI_NETWORK);
  const resolvedNetwork = parseNetwork(runtimeConfig?.network) ?? envNetwork ?? "testnet";
  const CETUS_GLOBAL_CONFIG = (
    runtimeConfig?.globalConfig
      ?? process.env.REACT_APP_CETUS_GLOBAL_CONFIG
      ?? DEFAULT_GLOBAL_CONFIG
  ).trim();
  const CLOCK_OBJECT = (
    runtimeConfig?.clockObject
      ?? process.env.REACT_APP_CLOCK_OBJECT
      ?? DEFAULT_CLOCK_OBJECT
  ).trim();
  const PLATFORM_FEE_MIST = useMemo(() => {
    const source = runtimeConfig?.platformFeeMist ?? process.env.REACT_APP_PLATFORM_FEE_MIST ?? "10000000";
    try {
      return BigInt(source);
    } catch {
      return 10000000n;
    }
  }, [runtimeConfig?.platformFeeMist]);
  const SWAP_GAS_BUDGET = useMemo(() => {
    const source = runtimeConfig?.gasBudget ?? Number(process.env.REACT_APP_SWAP_GAS_BUDGET ?? "100000000");
    return Number.isFinite(source) && source > 0 ? source : 100_000_000;
  }, [runtimeConfig?.gasBudget]);

  const suiClient = useMemo(
    () => new SuiClient({ url: getFullnodeUrl(resolvedNetwork) }),
    [resolvedNetwork]
  );

  const SUI_COIN_TYPE = runtimeConfig?.suiCoinType ?? "0x2::sui::SUI";

  const resolvedWalletAddress = currentAccount?.address || user?.wallet_address || user?.walletAddress || null;
  const isConfigReady = configLoaded && Boolean(CETUS_GLOBAL_CONFIG) && Boolean(CLOCK_OBJECT);

  const ensureEnvConfigured = useCallback(() => {
    if (!configLoaded) {
      throw new Error("Swap configuration still loading. Please wait a moment.");
    }
    if (!CETUS_GLOBAL_CONFIG) {
      throw new Error("Swap configuration missing global config object. Contact support.");
    }
    if (!CLOCK_OBJECT) {
      throw new Error("Swap configuration missing clock object. Contact support.");
    }
  }, [CETUS_GLOBAL_CONFIG, CLOCK_OBJECT, configLoaded]);

  useEffect(() => {
    let isMounted = true;
    const loadConfig = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/swaps/config`);
        if (!response.ok) {
          throw new Error(`Failed to load swap config (${response.status})`);
        }
        const payload = (await response.json()) as { success?: boolean; config?: SwapRuntimeConfig };
        if (isMounted && payload?.success && payload.config) {
          setRuntimeConfig(payload.config);
        }
      } catch (err) {
        console.warn("Unable to load swap runtime config", err);
      } finally {
        if (isMounted) {
          setConfigLoaded(true);
        }
      }
    };

    loadConfig();
    return () => {
      isMounted = false;
    };
  }, [BACKEND_URL]);

  useEffect(() => {
    if (configLoaded && !isConfigReady) {
      setStatus({
        type: 'error',
        message:
          'Swap configuration missing on backend. Please contact support to configure CETUS_GLOBAL_CONFIG/CLOCK_OBJECT.',
      });
    }
  }, [configLoaded, isConfigReady]);

  const parseDecimalToUnits = useCallback((amount: string, decimals: number): bigint => {
    const trimmed = amount.trim();
    if (!trimmed) {
      throw new Error("Amount is required");
    }
    if (!/^\d+(\.\d+)?$/.test(trimmed)) {
      throw new Error("Invalid numeric amount");
    }
    const [whole, frac = ""] = trimmed.split(".");
    const truncated = frac.slice(0, decimals);
    const padded = truncated.padEnd(decimals, "0");
    return BigInt(whole || "0") * (10n ** BigInt(decimals)) + BigInt(padded || "0");
  }, []);

  const unitsToDecimalString = useCallback((value: bigint, decimals: number) => {
    const factor = 10n ** BigInt(decimals);
    const whole = value / factor;
    const fraction = value % factor;
    if (fraction === 0n) {
      return whole.toString();
    }
    let fractionStr = fraction.toString().padStart(decimals, "0");
    fractionStr = fractionStr.replace(/0+$/u, "");
    return `${whole.toString()}.${fractionStr}`;
  }, []);

  const computeLimitA2B = useCallback((current: bigint) => {
    const delta = current / 2000n;
    const adjustment = delta > 1n ? delta : 1n;
    let candidate = current > delta ? current - adjustment : current - 1n;
    const MIN_SQRT = 4295128739n;
    if (candidate <= MIN_SQRT) {
      return MIN_SQRT + 1n;
    }
    return candidate;
  }, []);

  const computeLimitB2A = useCallback((current: bigint) => {
    const delta = current / 2000n;
    let candidate = current + (delta > 1n ? delta : 1n);
    const MAX_SQRT = 1461446703485210103287273052203988822378723970342n;
    if (candidate > MAX_SQRT) candidate = MAX_SQRT;
    return candidate;
  }, []);

  const findSwapEvent = useCallback((events: any[] | undefined) => {
    if (!Array.isArray(events)) return null;
    const suffix = "::pool::SwapEvent";
    return events.find((event) => {
      if (!event?.type || typeof event.type !== "string") return false;
      if (!event.type.includes(suffix)) return false;
      if (tokenState.contractPackageId) {
        return event.type.includes(`${tokenState.contractPackageId}::`);
      }
      return true;
    }) ?? null;
  }, [tokenState.contractPackageId]);

  const pickCoinsForAmount = useCallback(
    async (owner: string, required: bigint, coinType: string = SUI_COIN_TYPE) => {
      const coinsRes = await suiClient.getCoins({ owner, coinType, limit: 200 });
      const coins = coinsRes.data || [];
      if (!coins.length) {
        throw new Error(`No coins of type ${coinType === SUI_COIN_TYPE ? "SUI" : coinType} found in wallet`);
      }
      const sorted = [...coins].sort((a, b) => (BigInt(b.balance) > BigInt(a.balance) ? 1 : -1));
      const selected: typeof sorted = [];
      let total = 0n;
      for (const coin of sorted) {
        const balance = BigInt(coin.balance);
        selected.push(coin);
        total += balance;
        if (total >= required) break;
      }
      if (total < required) {
        throw new Error(`Insufficient balance for requested amount in ${coinType === SUI_COIN_TYPE ? "SUI" : coinType}`);
      }
      return selected;
    },
    [suiClient]
  );

  const recordTrade = useCallback(async (digest: string, suiAmount: string, tokenAmount: string | null, direction: "a2b" | "b2a") => {
    try {
      const payload: Record<string, unknown> = {
        walletAddress: resolvedWalletAddress ?? currentAccount?.address,
        tokenId: Number(tokenState.id),
        tokenSymbol: tokenState.symbol,
        bondingCurveId: tokenState.bondingCurveId,
        contractPackageId: tokenState.contractPackageId,
        transactionType: direction === "b2a" ? "buy" : "sell",
        transactionHash: digest,
        suiAmount: Number(suiAmount),
      };
      if (tokenAmount !== null) {
        payload.tokenAmount = Number(tokenAmount);
      }

      const response = await fetch(`${BACKEND_URL}/trading`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();
      const tokenOut = data?.transaction?.token_amount ?? data?.transaction?.tokenAmount;
      if (tokenOut !== undefined && tokenOut !== null) {
        setToAmount(String(tokenOut));
      }
    } catch (err) {
      console.warn("Swap succeeded but failed to persist trade", err);
    }
  }, [BACKEND_URL, currentAccount?.address, resolvedWalletAddress, tokenState.bondingCurveId, tokenState.contractPackageId, tokenState.id, tokenState.symbol]);

  const handleSwap = useCallback(async () => {
    const trimmedAmount = fromAmount.trim();
    if (!trimmedAmount) {
      setStatus({ type: 'error', message: 'Enter an amount to swap' });
      return;
    }

    if (!resolvedWalletAddress || !currentAccount?.address) {
      setStatus({ type: 'error', message: 'Connect your wallet to continue' });
      return;
    }

    let activeToken = tokenState;

    if (!activeToken.poolId || !activeToken.swapPlatformFeeObj) {
      const refreshed = await refreshPoolMetadata();
      if (refreshed) {
        activeToken = refreshed;
      }
    }

    if (!activeToken.poolId || !activeToken.swapPlatformFeeObj) {
      setStatus({ type: 'error', message: 'Token is missing pool configuration. Please try again later.' });
      return;
    }

    ensureEnvConfigured();

    setIsSubmitting(true);
    setStatus({ type: 'loading', message: 'Executing swap...' });
    setToAmount("");

    try {
      const amountUnits = parseDecimalToUnits(trimmedAmount, swapDirection === "b2a" ? 9 : activeToken.decimals ?? 9);
      const tx = new TransactionBlock();
      tx.setGasBudget(SWAP_GAS_BUDGET);
      tx.setSender(currentAccount.address);

      const poolObject = await suiClient.getObject({
        id: activeToken.poolId,
        options: { showContent: true },
      });

      const poolContent = poolObject?.data?.content as { dataType?: string; fields?: Record<string, unknown> } | null | undefined;
      if (!poolContent || poolContent.dataType !== "moveObject") {
        throw new Error("Failed to load pool data");
      }

      const sqrtPriceRaw = (poolContent.fields?.current_sqrt_price ?? poolContent.fields?.currentSqrtPrice ?? "0") as string | number | bigint;
      const sqrtPrice = BigInt(typeof sqrtPriceRaw === "string" ? sqrtPriceRaw : sqrtPriceRaw.toString());
      const sqrtPriceLimit = swapDirection === "b2a" ? computeLimitB2A(sqrtPrice) : computeLimitA2B(sqrtPrice);

      let inputArg;
      let feeArg;
      if (swapDirection === "b2a") {
        const requiredMist = amountUnits + PLATFORM_FEE_MIST;
        const selectedCoins = await pickCoinsForAmount(currentAccount.address, requiredMist);
        const primaryCoin = selectedCoins[0];
        const primaryArg = tx.object(primaryCoin.coinObjectId);
        if (selectedCoins.length > 1) {
          tx.mergeCoins(
            primaryArg,
            selectedCoins.slice(1).map((coin) => tx.object(coin.coinObjectId))
          );
        }
        [inputArg, feeArg] = tx.splitCoins(primaryArg, [
          tx.pure(amountUnits),
          tx.pure(PLATFORM_FEE_MIST),
        ]);
        tx.moveCall({
          target: `${activeToken.contractPackageId}::swap_utils::swap_b2a`,
          typeArguments: [activeToken.coinType, SUI_COIN_TYPE],
          arguments: [
            tx.object(CETUS_GLOBAL_CONFIG),
            tx.object(activeToken.poolId),
            inputArg,
            feeArg,
            tx.object(activeToken.swapPlatformFeeObj),
            tx.pure(true),
            tx.pure(amountUnits),
            tx.pure(0),
            tx.pure(sqrtPriceLimit),
            tx.object(CLOCK_OBJECT),
          ],
        });
      } else {
        const tokenCoins = await suiClient.getCoins({ owner: currentAccount.address, coinType: activeToken.coinType, limit: 200 });
        const available = tokenCoins.data || [];
        if (!available.length) {
          throw new Error(`No ${activeToken.symbol} coins found in wallet`);
        }
        const sorted = [...available].sort((a, b) => BigInt(b.balance) > BigInt(a.balance) ? 1 : -1);
        let total = 0n;
        const toMerge: string[] = [];
        let primaryToken = sorted[0];
        total += BigInt(primaryToken.balance);
        for (let i = 1; total < amountUnits && i < sorted.length; i += 1) {
          const coin = sorted[i];
          toMerge.push(coin.coinObjectId);
          total += BigInt(coin.balance);
        }
        if (total < amountUnits) {
          throw new Error(`Insufficient ${activeToken.symbol} balance`);
        }
        const primaryArg = tx.object(primaryToken.coinObjectId);
        if (toMerge.length > 0) {
          tx.mergeCoins(primaryArg, toMerge.map((id) => tx.object(id)));
        }
        if (BigInt(primaryToken.balance) === amountUnits && toMerge.length === 0) {
          inputArg = primaryArg;
        } else {
          [inputArg] = tx.splitCoins(primaryArg, [tx.pure(amountUnits)]);
        }

        const feeCoins = await pickCoinsForAmount(currentAccount.address, PLATFORM_FEE_MIST, SUI_COIN_TYPE);
        const feePrimary = tx.object(feeCoins[0].coinObjectId);
        if (feeCoins.length > 1) {
          tx.mergeCoins(
            feePrimary,
            feeCoins.slice(1).map((coin) => tx.object(coin.coinObjectId))
          );
        }
        const [feeSplit] = tx.splitCoins(feePrimary, [tx.pure(PLATFORM_FEE_MIST)]);
        feeArg = feeSplit;
        tx.moveCall({
          target: `${activeToken.contractPackageId}::swap_utils::swap_a2b`,
          typeArguments: [activeToken.coinType, SUI_COIN_TYPE],
          arguments: [
            tx.object(CETUS_GLOBAL_CONFIG),
            tx.object(activeToken.poolId),
            inputArg,
            feeArg,
            tx.object(activeToken.swapPlatformFeeObj),
            tx.pure(true),
            tx.pure(amountUnits),
            tx.pure(0),
            tx.pure(sqrtPriceLimit),
            tx.object(CLOCK_OBJECT),
          ],
        });
      }

      const result = await signAndExecuteTransactionBlock({
        transactionBlock: tx,
        options: { showEffects: true, showEvents: true },
      });

      const digest = result.digest;
      setStatus({ type: 'success', message: 'Swap executed successfully', txDigest: digest });

      const txDetails = await suiClient.getTransactionBlock({
        digest,
        options: { showEvents: true },
      });
      const swapEvent = findSwapEvent(Array.isArray(txDetails?.events) ? txDetails?.events : undefined);
      let tokenOutAmount: string | null = null;
      if (swapEvent?.parsedJson) {
        const parsed = swapEvent.parsedJson as Record<string, unknown> | undefined;
        const rawOut = (parsed?.amount_out ?? parsed?.amountOut ?? "0") as string | number | bigint;
        const tokenUnits = BigInt(typeof rawOut === "string" ? rawOut : rawOut.toString());
        if (swapDirection === "b2a") {
          tokenOutAmount = unitsToDecimalString(tokenUnits, tokenState.decimals ?? 9);
          setToAmount(tokenOutAmount);
        } else {
          const suiAmount = unitsToDecimalString(tokenUnits, 9);
          setToAmount(suiAmount);
        }
      }

      const suiAmountRecorded = swapDirection === "b2a" ? trimmedAmount : unitsToDecimalString(BigInt((swapEvent?.parsedJson as any)?.amount_out ?? (swapEvent?.parsedJson as any)?.amountOut ?? "0"), 9);
      await recordTrade(digest, suiAmountRecorded, swapDirection === "b2a" ? tokenOutAmount : trimmedAmount, swapDirection);
    } catch (err: any) {
      console.error("Swap failed", err);
      setStatus({ type: 'error', message: err?.message || 'Failed to execute swap' });
      setToAmount("");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    CETUS_GLOBAL_CONFIG,
    CLOCK_OBJECT,
    PLATFORM_FEE_MIST,
    SWAP_GAS_BUDGET,
    computeLimitB2A,
    currentAccount?.address,
    ensureEnvConfigured,
    findSwapEvent,
    parseDecimalToUnits,
    pickCoinsForAmount,
    recordTrade,
    resolvedWalletAddress,
    signAndExecuteTransactionBlock,
    suiClient,
    tokenState.coinType,
    tokenState.contractPackageId,
    tokenState.decimals,
    tokenState.poolId,
    tokenState.swapPlatformFeeObj,
    swapDirection,
    unitsToDecimalString,
    fromAmount,
  ]);

  return (
    <div className="simple-swap-container">
      <div className="simple-swap-card">
        {/* Tabs */}
        <div className="swap-tabs">
          <button
            className={`swap-tab ${activeTab === "swap" ? "active" : ""}`}
            onClick={() => setActiveTab("swap")}
          >
            Swap
          </button>
          <button
            className={`swap-tab ${activeTab === "limit" ? "active" : ""}`}
            onClick={() => setActiveTab("limit")}
          >
            Limit
          </button>
          <div className="swap-slippage">
            <span className="slippage-value">{slippage}%</span>
            <span className="slippage-icon">⚙️</span>
          </div>
        </div>

        {/* From Section */}
        <div className="swap-section">
          <div className="swap-section-header">
            <span className="swap-label">From</span>
            <div className="swap-balance">
              <span className="balance-icon">💰</span>
              <span className="balance-text">0.00</span>
              <span className="balance-unit">Half</span>
              <span className="balance-max">Max</span>
            </div>
          </div>
          <div className="swap-input-row">
            <button className="token-selector">
              <span className="token-icon">{swapDirection === "b2a" ? SUI_ICON : tokenState.image ? "🪙" : "💎"}</span>
              <span className="token-name">{swapDirection === "b2a" ? SUI_SYMBOL : tokenState.symbol}</span>
              <span className="dropdown-arrow">▼</span>
            </button>
            <input
              type="text"
              className="swap-input"
              placeholder="0"
              value={fromAmount}
              onChange={(e) => setFromAmount(e.target.value)}
            />
          </div>
          <div className="swap-value">
            <span className="value-icon">●</span>
            <span className="value-text">$2.59092</span>
            <span className="value-change positive">+0.53%</span>
          </div>
          <div className="swap-arrow-container">
            <button
              className="swap-arrow-button"
              type="button"
              onClick={() => {
                setFromAmount(toAmount);
                setToAmount(fromAmount);
                setSwapDirection((prev) => (prev === "b2a" ? "a2b" : "b2a"));
              }}
            >
              ⇅
            </button>
          </div>
        </div>

        {/* To Section */}
        <div className="swap-section">
          <div className="swap-section-header">
            <span className="swap-label">To</span>
          </div>
          <div className="swap-input-row">
            <button className="token-selector">
              <span className="token-icon">{swapDirection === "b2a" ? (tokenState.image ? "🪙" : "💎") : SUI_ICON}</span>
              <span className="token-name">{swapDirection === "b2a" ? tokenState.symbol : SUI_SYMBOL}</span>
              <span className="dropdown-arrow">▼</span>
            </button>
            <input
              type="text"
              className="swap-input"
              placeholder="0"
              value={toAmount}
              onChange={(e) => setToAmount(e.target.value)}
              readOnly
            />
          </div>
          <div className="swap-value">
            <span className="value-icon">●</span>
            <span className="value-text">$0.0218560</span>
            <span className="value-change negative">-1.63%</span>
          </div>
        </div>

        {/* Trade Button */}
        <button
          className="trade-button"
          onClick={handleSwap}
          disabled={isSubmitting || !isConfigReady}
        >
          {isSubmitting ? "Processing..." : isConfigReady ? "Trade" : "Config Missing"}
        </button>

        {status.type !== 'idle' && (
          <div className={`swap-status-bar swap-status-${status.type}`}>
            <div className="swap-status-indicator">
              <span className="status-icon" aria-hidden="true">
                {status.type === 'loading' && '⟳'}
                {status.type === 'success' && '✅'}
                {status.type === 'error' && '⚠️'}
              </span>
              <div className="status-text">
                <strong>{status.type === 'loading' ? 'Processing' : status.type === 'success' ? 'Success' : 'Issue'}</strong>
                <span>{status.message}</span>
              </div>
            </div>
            {status.type === 'loading' && (
              <div className="status-progress">
                <div className="status-progress-bar" />
              </div>
            )}
            {status.type === 'success' && status.txDigest && (
              <div className="status-actions">
                <a
                  href={`https://suiscan.xyz/testnet/tx/${status.txDigest}`}
                  target="_blank"
                  rel="noreferrer"
                  className="status-link"
                >
                  View transaction ↗
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Featured Pools Section */}
      <div className="featured-pools-section">
        <h3 className="featured-title">Featured Yield Farming Pools</h3>
        <div className="pools-grid">
          <div className="pool-card">
            <div className="pool-icons">
              <span className="pool-icon">🔴</span>
              <span className="pool-icon">💵</span>
            </div>
            <div className="pool-info">
              <div className="pool-pair">IKA / USDC</div>
              <div className="pool-apr">
                <span className="apr-label">APR</span>
                <span className="apr-value positive">711.77%</span>
              </div>
            </div>
          </div>

          <div className="pool-card">
            <div className="pool-icons">
              <span className="pool-icon">🔵</span>
              <span className="pool-icon">💵</span>
            </div>
            <div className="pool-info">
              <div className="pool-pair">DEEP / USDC</div>
              <div className="pool-apr">
                <span className="apr-label">APR</span>
                <span className="apr-value positive">766.30%</span>
              </div>
            </div>
          </div>

          <div className="pool-card">
            <div className="pool-icons">
              <span className="pool-icon">💧</span>
              <span className="pool-icon">💵</span>
            </div>
            <div className="pool-info">
              <div className="pool-pair">SUI / USDC</div>
              <div className="pool-apr">
                <span className="apr-label">APR</span>
                <span className="apr-value positive">60.90%</span>
              </div>
            </div>
          </div>

          <div className="pool-card">
            <div className="pool-icons">
              <span className="pool-icon">🟡</span>
              <span className="pool-icon">⚪</span>
            </div>
            <div className="pool-info">
              <div className="pool-pair">LBTC / WBTC</div>
              <div className="pool-apr">
                <span className="apr-label">APR</span>
                <span className="apr-value positive">9.88%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleSwapInterface;
